import { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import ModalPopup from '../ModalPopup/ModalPopup'
import Datalist from '../FormComponents/Datalist/Datalist'
import { ButtonSize, ButtonStyles, ButtonGroupAlignment, ModalPopupSize, InputTypes } from 'lib/enums'
import Input from 'components/FormComponents/Input/Input'
import type { QuickStartProject } from 'lib/types'
import { useAppSelector } from 'reducers/hooks'

type Option = { value: string; label?: string }

export type QuickStartState = {
  open: boolean
  openModal: () => void
  closeModal: () => void
  submit: QuickStartModalProps['onSubmit']
  projectOptions: QuickStartModalProps['projectOptions']
  siteOptions: QuickStartModalProps['siteOptions']
  quickStartProjects?: Record<string, QuickStartProject>
  submitting: boolean
}

export type QuickStartModalProps = {
  open: boolean
  onClose: () => void
  onSubmit: (payload: { projectName: string; siteName: string; siteDesignName: string }) => Promise<void> | void
  projectOptions: Array<string | Option>
  siteOptions: Array<string | Option>
  quickStartProjects?: Record<string, QuickStartProject>
  initialProject?: string
  initialSite?: string
  initialSiteDesign?: string
  small?: boolean
  className?: string
}

const QuickStartModal: React.FC<QuickStartModalProps> = ({
  open,
  onClose,
  onSubmit,
  projectOptions,
  siteOptions,
  quickStartProjects,
  initialProject = '',
  initialSite = '',
  initialSiteDesign = '',
  small,
  className
}) => {
  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const [projectName, setProjectName] = useState(initialProject)
  const [siteName, setSiteName] = useState(initialSite)
  const [siteDesignName, setSiteDesignName] = useState(initialSiteDesign)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null) // Currently unused

  const backendErrors = useAppSelector(state => state.toolsAction.backendErrors)

  const firstFieldRef = useRef<HTMLDivElement | null>(null)

  const canSubmit = Boolean(siteDesignName.trim()) && !submitting
  const datalistFilter = (input: string, opt: { value: string; label?: string }) => {
    const search = input.toLowerCase()
    return opt.label?.toLowerCase().includes(search) || opt.value.toLowerCase().includes(search)
  }

  // Derive site options dynamically from quickStartProjects when a project is selected
  const computedSiteOptions = useMemo(() => {
    if (quickStartProjects && projectName && quickStartProjects[projectName]?.sites) {
      const sites = quickStartProjects[projectName].sites || {}
      return Object.values(sites)
        .filter(s => s?.name)
        .map(s => ({ value: s.name, label: s.name }))
    }
    return siteOptions
  }, [projectName, quickStartProjects, siteOptions])

  const reset = () => {
    if (!isMountedRef.current) return
    setProjectName(initialProject)
    setSiteName(initialSite)
    setSiteDesignName(initialSiteDesign)
    setError(null)
  }

  const handleClose = () => {
    if (submitting) return
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        projectName: projectName.trim(),
        siteName: siteName.trim(),
        siteDesignName: siteDesignName.trim()
      })
      // Parent submit may navigate away and unmount this modal.
      // Guard local state updates to avoid setState on unmounted.
      if (isMountedRef.current) {
        reset()
        onClose()
      }
    } catch (e: any) {
      if (isMountedRef.current) setError(e?.message || 'Something went wrong')
    } finally {
      if (isMountedRef.current) setSubmitting(false)
    }
  }

  return (
    <ModalPopup
      id="quick-start-modal"
      headline="Enter Design Data"
      size={small ? ModalPopupSize.Small : ModalPopupSize.Medium}
      showModal={open}
      onCloseButtonclick={handleClose}
      buttonGroupAlignment={ButtonGroupAlignment.Right}
      button2Label="Cancel"
      button2Style={ButtonStyles.Secondary}
      button2Size={ButtonSize.Medium}
      button2OnClickHandler={handleClose}
      button1Label={submitting ? 'Submitting…' : 'Submit'}
      button1Style={ButtonStyles.Primary}
      button1Size={ButtonSize.Medium}
      button1OnClickHandler={handleSubmit}
      button1Disabled={!canSubmit}>
      <div className={classNames('qs-modal__body mt-4', className)}>
        <div ref={firstFieldRef} className="qs-modal__field mb-4">
          <Datalist
            id="qs-project"
            label="Project"
            placeholder="Quick Start Project"
            suggestions={projectOptions}
            value={projectName}
            onChange={setProjectName}
            defaultValue=""
            isDisabled={false}
            isSmall={false}
            className="w-full"
            maxLength={100}
            required={false}
            filterFn={datalistFilter}
            closeOnSelect={true}
          />
        </div>

        <div className="qs-modal__field mb-4">
          <Datalist
            id="qs-site"
            label="Site"
            placeholder="Quick Start Site"
            suggestions={computedSiteOptions}
            value={siteName}
            onChange={setSiteName}
            defaultValue=""
            isDisabled={false}
            isSmall={false}
            required={false}
            className="w-full"
            maxLength={50}
            filterFn={datalistFilter}
            closeOnSelect={true}
          />
        </div>

        <div className="qs-modal__field mb-2">
          <Input
            id="qs-sitedesign"
            type={InputTypes.Text}
            label="Site design*"
            placeholder="Quick Start SiteDesign"
            initialValue={siteDesignName}
            handleDataChange={(val: string) => setSiteDesignName(val)}
            isDisabled={false}
            isSmall={false}
            maxLength={50}
            required={true}
            className="w-full"
            onEventType="onChange"
          />
        </div>

        <div className="msds-text-body-2 msds-text-gray-8 mb-3">Fields marked * are required. Others are optional and auto-assigned if blank.</div>

        {(backendErrors.getProjectError || backendErrors.postSiteDesignError) && (
          <div className="msds-text-body-2 msds-text-danger-red-1 mt-2">
            ERROR: {backendErrors.getProjectError} {backendErrors.postSiteDesignError}
          </div>
        )}
      </div>
    </ModalPopup>
  )
}

export default QuickStartModal
